/**
 * Tier 2: Content Controls for signature field placement (all platforms).
 *
 * Inserts styled Content Controls with `kobsign:sig:` tag prefix at the
 * cursor position. The CC is displayed as a colored bounding box with the
 * signer's name — giving clear visual feedback in the document.
 *
 * Content Controls are inline (not freely draggable), so coordinates use
 * the page=0 sentinel — the backend detects position from the converted PDF.
 *
 * Pattern follows useContentControls.ts (Phase 5c.2).
 */

import type { SignaturePlacement } from './useSignatureTags'
import { devLog } from '../services/devLog'

const SIG_CC_PREFIX = 'kobsign:sig:'

/** Insert a signature Content Control at the current cursor position */
export async function insertSignatureControl(
  email: string,
  name: string,
  colorHex: string,
): Promise<void> {
  await Word.run(async (context) => {
    const selection = context.document.getSelection()

    // Insert visible label text at the cursor — gives the CC actual content
    const label = ` \u270D ${name} `
    const labelRange = selection.insertText(label, Word.InsertLocation.replace)

    // Wrap the label text with a Content Control
    const cc = labelRange.insertContentControl()

    cc.tag = `${SIG_CC_PREFIX}${email}`
    cc.title = '' // Keep title empty — name is shown in CC content

    // Enhanced styling — applied best-effort
    try { cc.color = colorHex } catch { /* color requires WordApi 1.3+ */ }
    try { cc.appearance = Word.ContentControlAppearance.boundingBox } catch {}
    try { cc.cannotDelete = true } catch {}
    // NOTE: do NOT set cannotEdit — it traps the cursor inside the CC

    await context.sync()

    // Move cursor after the CC so user can continue typing
    const afterRange = cc.getRange(Word.RangeLocation.after)
    afterRange.insertText(' ', Word.InsertLocation.after)
    afterRange.select(Word.SelectionMode.end)
    await context.sync()

    devLog.info('SigCC', `Inserted signature control for ${name} (${email})`)
  })
}

/** Read all signature Content Controls — returns page=0 placements */
export async function readSignatureControls(): Promise<SignaturePlacement[]> {
  return await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag,items/text')
    await context.sync()

    const placements: SignaturePlacement[] = []
    for (const cc of controls.items) {
      if (!cc.tag?.startsWith(SIG_CC_PREFIX)) continue

      const email = cc.tag.slice(SIG_CC_PREFIX.length)
      const name = cc.text?.replace(/\u270D\s?/, '').trim() || email

      // Content Controls have no spatial coordinates — use page=0 sentinel
      placements.push({
        email,
        name,
        page: 0,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
      })
    }

    devLog.info('SigCC', `Read ${placements.length} signature control(s)`)
    return placements
  })
}

/** Remove a specific signer's signature Content Control(s) */
export async function removeSignatureControl(email: string): Promise<void> {
  await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag')
    await context.sync()

    const target = `${SIG_CC_PREFIX}${email}`
    for (const cc of controls.items) {
      if (cc.tag === target) {
        try { cc.cannotDelete = false } catch {}
        cc.delete(false) // false = delete content too
      }
    }
    await context.sync()
    devLog.info('SigCC', `Removed signature control(s) for ${email}`)
  })
}

/** Remove all signature Content Controls from the document */
export async function removeSignatureControls(): Promise<void> {
  await Word.run(async (context) => {
    const controls = context.document.body.contentControls
    controls.load('items/tag')
    await context.sync()

    let count = 0
    for (const cc of controls.items) {
      if (!cc.tag?.startsWith(SIG_CC_PREFIX)) continue
      try { cc.cannotDelete = false } catch {}
      cc.delete(false)
      count++
    }
    await context.sync()
    devLog.info('SigCC', `Removed ${count} signature control(s)`)
  })
}
